export const apiEndpoints = {
  auth: {
    signin: 'auth/signin',
    selectProfile: 'auth/select-profile',
    switchProfile: 'auth/switch-profile',
    profiles: 'auth/profiles',
    impersonate: 'auth/impersonate',
  },

  activities: {
    base: 'activities',
    cities: 'activities/cities',
    detail: (benefitId: string) => `activities/${benefitId}`,
    duplicate: (benefitId: string) => `activities/duplicate/${benefitId}`,
  },

  benefits: {
    base: 'benefits',
    drafts: 'benefits/drafts',
    countries: 'benefits/countries',
    types: 'benefits/types',
    suppliers: 'benefits/suppliers',
    published: 'benefits/published',
    cities: 'benefits/cities',
    hotelAssociated: 'benefits/hotel-associated-benefits',
    circuits: (benefitId: string) => `benefits/circuits/${benefitId}`,
    detail: (benefitId: string) => `benefits/${benefitId}`,
    multiple: 'benefits/multiple',
    duplicate: (benefitId: string) => `benefits/duplicate/${benefitId}`,
  },

  bills: {
    base: 'bills',
  },

  trash: {
    count: 'trash/count',
    benefits: 'trash/benefits',
    circuits: 'trash/circuits',
    restoreBenefits: 'trash/benefits/restore',
    restoreCircuits: 'trash/circuits/restore',
  },

  carRentals: {
    base: 'car-rentals',
    detail: (benefitId: string) => `car-rentals/${benefitId}`,
    duplicate: (benefitId: string) => `car-rentals/duplicate/${benefitId}`,
  },

  circuits: {
    base: 'circuits',
    distributors: 'circuits/distributors',
    distributorsInvalidated: 'circuits/distributors/invalidated',
    countries: 'circuits/countries',
    durations: 'circuits/durations',
    drafts: 'circuits/drafts',
    detail: (circuitId: string) => `circuits/${circuitId}`,
    multiple: 'circuits/multiple',
    duplicate: (circuitId: string) => `circuits/duplicate/${circuitId}`,
    distribute: 'circuits/distribute',
    withdraw: (circuitId: string) => `circuits/withdraw/${circuitId}`,
    distributorsReceivers: 'circuits/distributors/receivers',
    distributorsCountries: 'circuits/distributors/countries',
    distributorsDurations: 'circuits/distributors/durations',
    originalImages: (circuitId: string) =>
      `circuits/original-images/${circuitId}`,
    editableCircuits: 'circuits/distributors/editable-circuits',
    editableCircuitDetail: (editableCircuitId: string) =>
      `circuits/distributors/editable-circuits/${editableCircuitId}`,
    import: (circuitCode: number, dayNumber: number) =>
      `circuits/import/${circuitCode}/${dayNumber}`,
    paragraphOverrides: (circuitId: string) =>
      `circuits/${circuitId}/paragraph-overrides`,
  },

  clients: {
    base: 'clients',
    detail: (clientId: string) => `clients/${clientId}`,
    multiple: 'clients/multiple',
  },

  currencyExchange: {
    latest: 'currency-exchange/latest',
    closestToDate: 'currency-exchange/closest-to-date',
    agencyRates: 'currency-exchange/agency-rates',
    effectiveRates: 'currency-exchange/effective-rates',
    useMarketRate: 'currency-exchange/agency-rates/use-market',
  },

  distributors: {
    base: 'distributors',
    networks: 'distributors/networks',
    countries: 'distributors/countries',
    circuits: 'distributors/circuits',
    allNetworks: 'distributors/AllNetworks',
    recent: 'distributors/recent',
    agencies: (networkId: string) => `distributors/agencies/${networkId}`,
    partnershipRequests: 'distributors/partnershipRequests',
    partnershipRequest: (distributorId: string) =>
      `distributors/partnershipRequest/${distributorId}`,
    shareCircuits: 'distributors/share/circuits',
    byCircuits: 'distributors/byCircuits',
    distributor: (distributorId: string) =>
      `distributors/distributor/${distributorId}`,
  },

  companies: {
    receivers: 'companies/receivers',
    allReceivers: 'companies/allReceivers',
    partnershipRequest: 'companies/partnershipRequest',
    partnershipRequests: 'companies/partnershipRequests',
    partnershipRequestDetail: (receiverId: string) =>
      `companies/partnershipRequest/${receiverId}`,
    company: (receiverId: string) => `companies/company/${receiverId}`,
    companyUpdate: (companyId: string) => `companies/company/${companyId}`,
    agency: (id: string) => `companies/agency/${id}`,
    agencyWebsite: (estimateId: string) =>
      `companies/agency/${estimateId}/website`,
    shouldShareAllCircuits: 'companies/shouldShareAllCircuits',
  },

  estimates: {
    byProject: (projectId: string) => `estimates/project/${projectId}`,
    detail: (projectId: string, estimateId: string) =>
      `estimates/project/${projectId}/estimate/${estimateId}`,
    duplicate: (projectId: string) =>
      `estimates/duplicate/project/${projectId}`,
    delete: (projectId: string, estimateId: string) =>
      `estimates/project/${projectId}/${estimateId}`,
    save: (projectId: string, estimateId: string) =>
      `estimates/project/${projectId}/${estimateId}`,
    reservation: (projectId: string, estimateId: string) =>
      `estimates/reservation/project/${projectId}/${estimateId}`,
    reservationRequests: 'estimates/receiver/reservation-requests',
    reservationRequestDetail: (estimateId: string) =>
      `estimates/receiver/reservation-requests/${estimateId}`,
    acceptReservation: (estimateId: string) =>
      `estimates/receiver/reservation-requests/${estimateId}/accept`,
    refuseReservation: (estimateId: string) =>
      `estimates/receiver/reservation-requests/${estimateId}/refuse`,
    sharedLink: (projectId: string, estimateId: string) =>
      `estimates/shared-link/${projectId}/${estimateId}`,
    shareLink: (estimateId: string) => `estimates/share-link/${estimateId}`,
    sharedLinkSave: (projectId: string, estimateId: string) =>
      `estimates/shared-link/save/${projectId}/${estimateId}`,
    sharedLinkSendReservation: (projectId: string, estimateId: string) =>
      `estimates/shared-link/send-reservation-request/${projectId}/${estimateId}`,
    sharedLinkRevoke: (estimateId: string) =>
      `estimates/shared-link/revoke/${estimateId}`,
    sharedLinkClientLink: (projectId: string, estimateId: string) =>
      `estimates/shared-link/client-link/${projectId}/${estimateId}`,
    sharedLinkClientLinkRevoke: (projectId: string, estimateId: string) =>
      `estimates/shared-link/client-link/revoke/${projectId}/${estimateId}`,
    info: (projectId: string, estimateId: string) =>
      `estimates/project/${projectId}/estimate/info/${estimateId}`,
    validateParagraph: (projectId: string, estimateId: string) =>
      `estimates/project/${projectId}/${estimateId}/validate-paragraph`,
  },

  s3: {
    signUrl: (key: string) => `s3/sign-url/${key}`,
  },

  flights: {
    base: 'flights',
    cities: 'flights/cities',
    airports: 'flights/airports',
    detail: (benefitId: string) => `flights/${benefitId}`,
    duplicate: (benefitId: string) => `flights/duplicate/${benefitId}`,
  },

  hotels: {
    base: 'hotels',
    cities: 'hotels/cities',
    chains: 'hotels/chains',
    detail: (benefitId: string) => `hotels/${benefitId}`,
    duplicate: (benefitId: string) => `hotels/duplicate/${benefitId}`,
  },

  projects: {
    byClient: (clientId: string) => `projects/client/${clientId}`,
    detail: (clientId: string, projectId: string) =>
      `projects/${clientId}/${projectId}`,
    create: (clientId: string) => `projects/client/${clientId}`,
    update: (clientId: string, projectId: string) =>
      `projects/${clientId}/${projectId}`,
    delete: (clientId: string, projectId: string) =>
      `projects/${clientId}/${projectId}`,
  },

  suppliers: {
    base: 'suppliers',
    drafts: 'suppliers/drafts',
    cities: 'suppliers/cities',
    countries: 'suppliers/countries',
    detail: (supplierId: string) => `suppliers/${supplierId}`,
    allMySuppliers: 'suppliers/allmysuppliers',
    duplicate: (supplierId: string) => `suppliers/${supplierId}/duplicate`,
    multiple: 'suppliers/multiple',
  },

  translation: {
    translate: 'translation/translate',
    translateMany: 'translation/translate-many',
  },

  users: {
    me: 'users/me',
    base: 'users',
    networks: 'users/networks',
    employees: 'users/employees',
    employee: (employeeId: string) => `users/employee/${employeeId}`,
    employeeUpdate: (employeeId: string) => `users/employees/${employeeId}`,
    employeesMultiple: 'users/employees/multiple',
  },

  vehiclesWithChauffeur: {
    base: 'vehicles-with-chauffeur',
    detail: (benefitId: string) => `vehicles-with-chauffeur/${benefitId}`,
    duplicate: (benefitId: string) =>
      `vehicles-with-chauffeur/duplicate/${benefitId}`,
  },

  admin: {
    receivers: 'stats/admin/receivers',
    distributors: 'stats/admin/distributors',
  },
} as const;
